/**
 * OtpInput Component
 *
 * 6-box banking-style OTP input with auto-advance, auto-back on backspace,
 * arrow key navigation, paste support, and focus-select behavior.
 *
 * Always rendered dir="ltr" — OTP codes are numeric and always left-to-right.
 */

import { useRef, useCallback } from 'react';
import type { KeyboardEvent, ClipboardEvent } from 'react';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

export function OtpInput({ value, onChange, disabled, autoFocus }: OtpInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || '');

  const focusIndex = (index: number) => {
    inputRefs.current[Math.max(0, Math.min(5, index))]?.focus();
  };

  const handleChange = useCallback(
    (index: number, inputValue: string) => {
      const sanitized = inputValue.replace(/\D/g, '').slice(-1);
      const newDigits = [...digits];
      newDigits[index] = sanitized;
      onChange(newDigits.join(''));
      if (sanitized && index < 5) {
        focusIndex(index + 1);
      }
    },
    [digits, onChange]
  );

  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[index] && index > 0) {
        focusIndex(index - 1);
      }
      if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        focusIndex(index - 1);
      }
      if (e.key === 'ArrowRight' && index < 5) {
        e.preventDefault();
        focusIndex(index + 1);
      }
    },
    [digits]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
      onChange(pasted.padEnd(6, '').slice(0, 6).trimEnd());
      // onChange with trimmed value so empty tail boxes stay empty
      const filled = pasted.slice(0, 6);
      onChange(filled);
      focusIndex(Math.min(filled.length, 5));
    },
    [onChange]
  );

  return (
    <div className="flex gap-3 justify-center" dir="ltr">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          autoFocus={autoFocus && index === 0}
          disabled={disabled}
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          className={`
            w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 transition-all
            focus:outline-none focus:ring-0
            ${digit
              ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930] dark:bg-[#004D40]/20 dark:text-[#B7DDD7]'
              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            focus:border-[#004D40]
          `}
        />
      ))}
    </div>
  );
}

export default OtpInput;
